import { lbBrand } from '@fuse/colors';
import FuseUtils from '@fuse/utils';
import authRoles from 'app/auth/authRoles';
import { userPermissionsSelector, userRoleSelector } from 'app/auth/store/user.selector';
import { isFeatureAllowed } from 'app/auth/utils/utils';
import { HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from 'app/constants/http.constant';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import { FranchiseFeatureType } from 'app/main/apps/franchises/type';
import { apiHttpStatus } from 'app/services/api/apiErrorStatus';
import { AppDispatch } from 'app/store';
import { clearNavigationBadge, setNavigationBadge } from 'app/store/fuse/navigationBadgesSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { BoosterNewPlayers, beaconBoosterVisit, getBoosterNewPlayersApi, recordBoosterVisitApi } from './api';
import { BOOSTER_OPTIONS, BoosterOption, formatIconCount, getOptionForPath } from './boosterOptions';
import { getVisitForTick, getVisitsForNavigation } from './boosterVisits';

const REFRESH_INTERVAL_MS = 60_000;

const isSessionRejected = (error: unknown) => {
  const status = apiHttpStatus(error);
  return status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN;
};

// Visits run one after the other, so the last response already counts every visit before it.
const requestNewPlayers = (visits: BoosterOption[]): Promise<BoosterNewPlayers> => {
  if (visits.length === 0) {
    return getBoosterNewPlayersApi();
  }
  const [first, ...rest] = visits;
  return rest.reduce(
    (previous, option) => previous.then(() => recordBoosterVisitApi(option)),
    recordBoosterVisitApi(first),
  );
};

export const BoosterNavBadges = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const role = useSelector(userRoleSelector);
  const permissions = useSelector(userPermissionsSelector);
  const isEnabled =
    Boolean(FuseUtils.hasPermission(authRoles.owner, role)) && isFeatureAllowed(FranchiseFeatureType.GAME, permissions);

  const [newPlayers, setNewPlayers] = useState<BoosterNewPlayers | null>(null);
  const pathRef = useRef<string | null>(null);
  const latestRequestRef = useRef(0);
  // A tab left open on a bundle that cannot recover the session would otherwise poll with a dead token forever.
  const sessionRejectedRef = useRef(false);

  const refresh = useCallback((visits: BoosterOption[]) => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    const isLatest = () => requestId === latestRequestRef.current;
    requestNewPlayers(visits).then(
      response => {
        sessionRejectedRef.current = false;
        if (isLatest()) setNewPlayers(response);
      },
      error => {
        if (isSessionRejected(error)) sessionRejectedRef.current = true;
        if (isLatest()) setNewPlayers(null);
      },
    );
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      pathRef.current = null;
      return;
    }
    const previousPath = pathRef.current;
    pathRef.current = pathname;
    refresh(getVisitsForNavigation(previousPath, pathname));
  }, [isEnabled, pathname, refresh]);

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }
    // Leaving or recovering a brand session swaps the stored token before `pagehide`; keep the one this page used.
    const sessionToken = window.localStorage.getItem(STORAGE_KEYS.JWT_ACCESS_TOKEN);
    const tickIfVisible = () => {
      if (document.visibilityState !== 'visible' || pathRef.current === null || sessionRejectedRef.current) return;
      const option = getVisitForTick(pathRef.current);
      refresh(option ? [option] : []);
    };
    const beaconCurrentVisit = () => {
      const option = pathRef.current === null ? null : getOptionForPath(pathRef.current);
      if (option) beaconBoosterVisit(option, sessionToken);
    };

    const interval = window.setInterval(tickIfVisible, REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', tickIfVisible);
    window.addEventListener('pagehide', beaconCurrentVisit);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tickIfVisible);
      window.removeEventListener('pagehide', beaconCurrentVisit);
      latestRequestRef.current += 1;
    };
  }, [isEnabled, refresh]);

  useEffect(() => {
    if (!isEnabled) return;
    const currentOption = getOptionForPath(pathname);
    BOOSTER_OPTIONS.forEach(({ option, navItemId, textKey }) => {
      const count = option === currentOption ? 0 : (newPlayers?.[option] ?? 0);
      dispatch(
        count > 0
          ? setNavigationBadge({
              id: navItemId,
              badge: {
                iconTitle: formatIconCount(count),
                title: t(textKey, { count, lng: i18n.language }),
                bg: lbBrand.red,
                fg: lbBrand.white,
                scope: 'franchise',
              },
            })
          : clearNavigationBadge(navItemId),
      );
    });
  }, [dispatch, isEnabled, pathname, newPlayers, t, i18n.language]);

  return null;
};
